import _ from 'lodash'
import cx from 'classnames'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import SimpleTooltips from './SimpleTooltips'
import MenuOwner from './MenuOwner'
import Timing from '../hoc/Timing'
import {stepFocus} from '../utils.js'

const SHOW_DELAY = 500
const HIDE_DELAY = 500

class InfoWindow extends React.Component {
  static displayName = 'InfoWindow'

  static propTypes = {
    children: T.node,
    className: T.string,
    style: T.object,
    menuAlign: T.oneOf(['top', 'right', 'bottom', 'left']),
    menuClassName: T.string,
    onDoubleClick: T.func,
    onMouseDown: T.func,
    onMouseEnter: T.func,
    onMouseLeave: T.func,
    onTouchEnd: T.func,
    onKeyDown: T.func,
    tagName: T.string,
    testId: T.string,
    windowContents: T.any, // React elements OR function for getting content
    ignoreBoundsClipping: T.bool,

    clearTimer: T.func,
    setTimer: T.func
  }

  static defaultProps = {
    tagName: 'span',
    testId: null,
    className: '',
    menuAlign: 'top',
    menuClassName: ''
  }

  constructor(props) {
    super(props)
    this.state = {
      isMenuOpen: false
    }
  }

  // componentWillMount() {
  //   // only called once now unless you need to update the menu
  //   const { props } = this
  //   let menu = () => {
  //     return (
  //       <InfoWindowMenu
  //         className={ `info_window_menu ${ props.menuAlign } ${ props.menuClassName }` }
  //         onMouseEnter={ this._onMouseEnter }
  //         onMouseLeave={ this._onMouseLeave }
  //       >{ props.windowContents }</InfoWindowMenu>
  //     )
  //   }

  //   props.renderMenu(menu)
  // }

  _bubbleEvent = (eventListener, e) => {
    if (
      this.props[eventListener] &&
      typeof this.props[eventListener] === 'function'
    ) {
      this.props[eventListener](e)
    }
  }

  _isNodeInMenu = node => {
    if (this._menuRef) {
      return this._menuRef.isNodeInMenuHierarchy(node)
    }
    return null
  }

  _onMouseEnter = e => {
    this.props.setTimer(this.openMenu, SHOW_DELAY, 'hover')
    this._bubbleEvent('onMouseEnter', e)
  }

  _onMouseLeave = e => {
    if (!this._isNodeInMenu(e.relatedTarget)) {
      this.props.setTimer(this.closeMenu, HIDE_DELAY, 'hover')
    }
    this._bubbleEvent('onMouseLeave', e)
  }

  _onMouseDown = e => {
    this.props.clearTimer('hover')
    this.openMenu()

    //prevent text selection on double-mousedown, but still allow drag-select
    if (this._dblClicking) e.preventDefault()
    this._dblClicking = true
    this.props.setTimer(
      () => {
        this._dblClicking = false
      },
      500,
      'dblclick'
    )
    this._bubbleEvent('onMouseDown', e)
  }

  _onTouchEnd = e => {
    this.props.clearTimer('hover')
    this.openMenu()
    this._bubbleEvent('onTouchEnd', e)
  }

  _onDoubleClick = e => {
    this._bubbleEvent('onDoubleClick', e)
  }

  // shared handler for keydown events on the main el and the infowindow contents
  _onKeyDown = e => {
    if (!e.isDefaultPrevented()) {
      const key = e.key
      if (/Escape|Tab/.test(key)) {
        // Close popup and return focus to main element
        stepFocus(ReactDOM.findDOMNode(this), 0)
        this.closeMenu()
      }
      // Arrow keys open the menu and then step focus through its contents
      else if (/Down|Up|Left|Right/.test(key)) {
        if (this._winContent) {
          stepFocus(this._winContent, /Down|Right/.test(key) ? 1 : -1)
        } else {
          this.openMenu()
        }
        e.preventDefault()
      }
    }

    // Invoke callback from parent
    const cb = this.props.onKeyDown
    if (cb) {
      cb(e)
    }
  }

  openMenu = () => {
    this.setState({
      isMenuOpen: true
    })
  }

  closeMenu = () => {
    this.setState({
      isMenuOpen: false
    })
    this.props.clearTimer('hover')
  }

  _getMenuPosition = () => {
    // Calculate the element's visible rect; by honoring container clipping we
    // avoid weirdly placed popups on long values that overflow their parent's bounds
    const { menuAlign, ignoreBoundsClipping } = this.props
    let el = ReactDOM.findDOMNode(this)
    const rect = _.pick(
      el.getBoundingClientRect(),
      'top',
      'right',
      'bottom',
      'left'
    )
    if (!ignoreBoundsClipping) {
      while ((el = el.parentNode) && el.nodeType === Node.ELEMENT_NODE) {
        if (
          getComputedStyle(el).overflow !== 'visible' &&
          el !== document.documentElement
        ) {
          const clipRect = el.getBoundingClientRect()
          rect.left = Math.max(rect.left, clipRect.left)
          rect.top = Math.max(rect.top, clipRect.top)
          rect.right = Math.min(rect.right, clipRect.right)
          rect.bottom = Math.min(rect.bottom, clipRect.bottom)
        }
      }
    }

    const round = Math.round
    return menuAlign === 'top' || menuAlign === 'bottom'
      ? [round((rect.left + rect.right) / 2), round(rect[menuAlign])]
      : [round(rect[menuAlign]), round((rect.top + rect.bottom) / 2)]
  }

  _bindMenuRef = el => {
    this._menuRef = el
  }

  _bindWinContentRef = el => {
    this._winContent = el
  }

  _renderMenu = () => {
    const { windowContents, menuAlign, menuClassName } = this.props
    return (
      <div
        className={`info_window_menu ${menuAlign} ${menuClassName}`}
        onMouseEnter={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        onKeyDown={this._onKeyDown}
        ref={this._bindWinContentRef}
      >
        <SimpleTooltips>
          {typeof windowContents === 'function' ? windowContents() : windowContents}
        </SimpleTooltips>
      </div>
    )
  }

  render() {
    const { children, style, testId, className, tagName, onKeyDown } = this.props
    const { isMenuOpen } = this.state
    const InfoWindowEl = tagName

    return (
      <MenuOwner
        className={cx('info_window', isMenuOpen && 'menu_open', className)}
        style={style}
        onClose={this.closeMenu}
        renderMenu={this._renderMenu}
        getMenuPosition={this._getMenuPosition}
        isMenuOpen={isMenuOpen}
        elementType="span"
        getRef={this._bindMenuRef}
      >
        <InfoWindowEl
          data-testid={testId}
          onMouseEnter={this._onMouseEnter}
          onMouseLeave={this._onMouseLeave}
          onMouseDown={this._onMouseDown}
          onTouchEnd={this._onTouchEnd}
          onDoubleClick={this._onDoubleClick}
          onKeyDown={this._onKeyDown}
          tabIndex="0"
          onFocus={this._onMouseEnter}
        >
          {children}
        </InfoWindowEl>
      </MenuOwner>
    )
  }
}

export default Timing(InfoWindow)
